import React, { useState, useEffect } from 'react';
import { Modal, Progress, Steps, Typography, Space, Alert, Button } from 'antd';
import { CheckCircleOutlined, LoadingOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Text, Title } = Typography;
const { Step } = Steps;

interface ProgressStep {
  title: string;
  status: 'wait' | 'process' | 'finish' | 'error';
  description?: string;
  icon?: React.ReactNode;
}

interface ServerInstallProgressProps {
  visible: boolean;
  onClose: () => void;
  serverId?: number | null;
  serverName?: string;
  socket?: any;
}

const ServerInstallProgress: React.FC<ServerInstallProgressProps> = ({
  visible,
  onClose,
  serverId,
  serverName,
  socket
}) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [steps, setSteps] = useState<ProgressStep[]>([
    {
      title: '验证订单',
      status: 'wait',
      description: '正在验证订单信息...',
      icon: <LoadingOutlined />
    },
    {
      title: '创建目录结构',
      status: 'wait',
      description: '正在创建服务器目录...',
      icon: <LoadingOutlined />
    },
    {
      title: '复制配置文件',
      status: 'wait',
      description: '正在复制游戏配置文件...',
      icon: <LoadingOutlined />
    },
    {
      title: '更新服务器设置',
      status: 'wait',
      description: '正在配置服务器参数...',
      icon: <LoadingOutlined />
    },
    {
      title: '创建启动脚本',
      status: 'wait',
      description: '正在生成启动脚本...',
      icon: <LoadingOutlined />
    },
    {
      title: '完成安装',
      status: 'wait',
      description: '服务器安装完成',
      icon: <CheckCircleOutlined />
    }
  ]);
  const [currentStatus, setCurrentStatus] = useState<'installing' | 'success' | 'error'>('installing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (visible && serverId) {
      console.log('进度条组件初始化:', { visible, serverId, serverName });
      // 重置状态
      setCurrentStep(0);
      setProgress(0);
      setCurrentStatus('installing');
      setErrorMessage('');
      
      // 更新步骤状态
      setSteps(prev => prev.map((step, index) => ({
        ...step,
        status: index === 0 ? 'process' : 'wait',
        icon: index === 0 ? <LoadingOutlined /> : step.icon
      })));
    }
  }, [visible, serverId]);

  // 监听WebSocket进度更新
  useEffect(() => {
    if (!socket || !visible) return;

    const handleProgressUpdate = (data: any) => {
      if (data.serverId === serverId) {
        setProgress(data.progress);
        setCurrentStep(data.step);
        
        if (data.status === 'success') {
          setCurrentStatus('success');
          setSteps(prev => prev.map(step => ({
            ...step,
            status: 'finish',
            icon: <CheckCircleOutlined />
          })));
        } else if (data.status === 'error') {
          setCurrentStatus('error');
          setErrorMessage(data.message);
          setSteps(prev => prev.map((step, index) => ({
            ...step,
            status: index <= data.step ? 'error' : 'wait',
            icon: index <= data.step ? <ExclamationCircleOutlined /> : step.icon
          })));
        } else {
          // 更新当前步骤状态
          setSteps(prev => prev.map((step, index) => {
            if (index < data.step) {
              return { ...step, status: 'finish', icon: <CheckCircleOutlined /> };
            } else if (index === data.step) {
              return { ...step, status: 'process', icon: <LoadingOutlined /> };
            } else {
              return step;
            }
          }));
        }
      }
    };

    socket.on('server-install-progress', handleProgressUpdate);

    return () => {
      socket.off('server-install-progress', handleProgressUpdate);
    };
  }, [socket, visible, serverId]);

  const handleClose = () => {
    if (currentStatus === 'success') {
      onClose();
    }
  };

  const renderContent = () => {
    if (currentStatus === 'error') {
      return (
        <div className="text-center py-8">
          <ExclamationCircleOutlined className="text-red-500 text-6xl mb-4" />
          <Title level={4} className="text-red-600">安装失败</Title>
          <Text type="danger">{errorMessage}</Text>
        </div>
      );
    }

    if (currentStatus === 'success') {
      return (
        <div className="text-center py-8">
          <CheckCircleOutlined className="text-green-500 text-6xl mb-4" />
          <Title level={4} className="text-green-600">安装完成</Title>
          <Text className="block mb-6">服务器 "{serverName}" 已成功创建并准备就绪！</Text>
          <Button 
            type="primary" 
            size="large"
            onClick={() => {
              onClose();
              navigate('/servers');
            }}
          >
            查看我的服务器
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="text-center">
          <Title level={4}>正在安装服务器 "{serverName}"</Title>
          <Text type="secondary">请稍候，正在为您创建ARK服务器...</Text>
        </div>

        <div>
          <Progress 
            percent={Math.round(progress)} 
            status={currentStatus === 'installing' ? 'active' : 'success'}
            strokeColor={{
              '0%': '#108ee9',
              '100%': '#87d068',
            }}
          />
        </div>

        <Steps
          direction="vertical"
          current={currentStep}
          size="small"
        >
          {steps.map((step, index) => (
            <Step
              key={index}
              title={step.title}
              description={step.description}
              icon={step.icon}
              status={step.status}
            />
          ))}
        </Steps>

        <Alert
          message="安装提示"
          description="服务器安装过程可能需要几分钟时间，请耐心等待。安装完成后您将收到通知。"
          type="info"
          showIcon
        />
      </div>
    );
  };

  console.log('进度条组件渲染:', { visible, serverId, serverName, currentStatus, progress });
  
  return (
    <Modal
      title="服务器安装进度"
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={600}
      closable={currentStatus === 'success'}
      maskClosable={false}
    >
      {renderContent()}
    </Modal>
  );
};

export default ServerInstallProgress;
